import { CheckIcon, ChevronLeftIcon } from '@heroicons/react/outline';
import { Button, ConfigProvider, Steps, theme, ThemeConfig } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import Cookies from 'js-cookie';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';

import { programmaticScrollTo } from '@/utils/ScrollControl/scrollControl';

import PersonalityStart from './PersonalityStart';
import ResultPage from './ResultPage';
import { authModalControl, tokenUpdate } from '../../atoms/authModalControl';
import { user } from '../../atoms/user';
import ButtonDefault, {
  ButtonVariants,
} from '../../components/shared/basic/button';
import { notify } from '../../components/shared/basic/notify';
import { ShouldRender } from '../../components/shared/basic/ShouldRender';
import { formateErrorObject } from '../../components/shared/formateErrorObject';
import CustomLoader from '../../components/shared/Loading';
import ForgotPasswordModal from '../../components/V2/ForgotPasswordModal';
import LoginModal from '../../components/V2/LoginModal';
import SignUpModal from '../../components/V2/SignUpModal';
import appwriteService from '../../config/appwrite';
import { EventDetails, logEvent } from '../../hooks/useClickEventTracking';
import {
  getCategoriesAPI,
  getQuestionsAPI,
  getUserPersonalityAPI,
  IPersonalityTestPayload,
  postPersonalityResponse,
} from '../../services/personalityTest';
import { User } from '../../utils/interfaces/User';
import { ZebraLearnLogo } from '../../../assets';

interface Question {
  questionId: string;
  text: string;
  options: { optionId: string; text: string }[];
}

interface StepData {
  categoryId: string;
  title: string;
  questions: Question[];
  formType?: string;
}

interface Answer {
  questionId: string;
  optionId: string;
}

const COOKIE_NAME = 'personalityTestAnswers';
const COOKIE_EXPIRATION_HOURS = 1;

const config: ThemeConfig = {
  token: { colorPrimary: '#FFFFFF', fontFamily: 'inherit' },
};

const TestPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, string>
  >({});
  const [results, setResults] = useState<string[]>();
  const [visible, setVisible] = useRecoilState(authModalControl);
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);
  const [unansweredQuestions, setUnansweredQuestions] = useState<string[]>([]);

  const screens = useBreakpoint();
  const questionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  const [stepsData, setStepsData] = useState<StepData[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitResponseLoading, setSubmitResponseLoading] = useState(false);
  const [userData, setUserData] = useRecoilState<User | undefined>(user);
  const router = useRouter();

  const loadSavedAnswers = () => {
    const savedAnswers = Cookies.get(COOKIE_NAME);
    if (savedAnswers) {
      try {
        const parsedAnswers: Answer[] = JSON.parse(savedAnswers);
        setAnswers(parsedAnswers);

        const selected = parsedAnswers.reduce(
          (acc, { questionId, optionId }) => ({
            ...acc,
            [questionId]: optionId,
          }),
          {},
        );
        setSelectedOptions(selected);

        const firstIncompleteStep = stepsData.findIndex((step) =>
          step.questions.some(
            (q) => !parsedAnswers.some((a) => a.questionId === q.questionId),
          ),
        );

        setCurrentStep(
          firstIncompleteStep === -1
            ? stepsData.length - 1
            : firstIncompleteStep,
        );
      } catch (error) {
        console.error('Error parsing saved answers from cookies:', error);
      }
    } else {
      setCurrentStep(0);
    }
  };

  const saveAnswersToCookies = (updatedAnswers: Answer[]) => {
    Cookies.set(COOKIE_NAME, JSON.stringify(updatedAnswers), {
      expires: COOKIE_EXPIRATION_HOURS / 24,
    });
  };

  const fetchStepsData = async () => {
    setLoading(true);
    try {
      const categoriesResponse = await getCategoriesAPI();
      const categories = categoriesResponse?.data || [];

      const categoryIds = categories.map((category: any) => ({
        id: category._id,
        name: category.name,
      }));

      const questionsResponses = await Promise.all(
        categoryIds.map(({ id }: { id: string }) => getQuestionsAPI(id)),
      );

      const formattedStepsData: StepData[] = categoryIds.map(
        ({ id, name }: { id: string; name: string }, index: number) => {
          const questionsData = questionsResponses[index]?.data?.data || [];
          return {
            categoryId: id,
            title: name,
            questions: questionsData.map((question: any) => ({
              questionId: question._id,
              text: question.text,
              options: question.options.map((option: any) => ({
                optionId: option._id,
                text: option.label,
              })),
            })),
          };
        },
      );

      const startingStep: StepData = {
        categoryId: 'starting-page',
        title: 'Welcome to the Personality Test',
        questions: [],
        formType: 'starting-page',
      };

      setStepsData([startingStep, ...formattedStepsData]);
      setLoading(false);

      return formattedStepsData;
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
      return undefined;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (stepsData.length > 0) {
      loadSavedAnswers();
    }
  }, [stepsData]);

  const handleOptionChange = (
    questionId: string,
    optionId: string,
    index: number,
  ) => {
    setSelectedOptions((prev) => ({ ...prev, [questionId]: optionId }));
    const updatedAnswers = answers.filter(
      (answer) => answer.questionId !== questionId,
    );
    updatedAnswers.push({ questionId, optionId });
    setAnswers(updatedAnswers);
    saveAnswersToCookies(updatedAnswers);

    setUnansweredQuestions((prev) =>
      prev.filter((item) => item !== questionId),
    );

    const nextIndex = index + 1;
    if (
      stepsData[currentStep] &&
      nextIndex < stepsData[currentStep]?.questions.length
    ) {
      const nextRefKey = `${currentStep}-${index + 1}`;
      const nextElement = questionRefs.current[nextRefKey];

      if (nextElement) {
        nextElement.style.scrollMarginTop = screens.md ? '100px' : '120px';
        nextElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleNext = () => {
    if (
      stepsData?.length &&
      stepsData[currentStep]?.categoryId === 'starting-page'
    ) {
      handleScrollToTop();
      setCurrentStep(currentStep + 1);
      return;
    }

    const currentQuestions = stepsData[currentStep]?.questions || [];
    const unansweredIds = currentQuestions
      .filter((q) => !answers.some((a) => a.questionId === q.questionId))
      .map((q) => q.questionId);

    if (unansweredIds.length > 0) {
      setUnansweredQuestions(unansweredIds);

      const questionElement =
        questionRefs.current[
        `${currentStep}-${currentQuestions.findIndex((q) =>
          unansweredIds.includes(q.questionId),
        )}`
        ];
      if (questionElement) {
        questionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      notify('Answer all questions before proceeding.', 'warning');
      return;
    }

    setUnansweredQuestions([]);
    handleSubmit();
  };

  const handleSubmit = async () => {
    const body = {
      categoryId: stepsData[currentStep]?.categoryId,
      answers,
    };

    await handleSubmitResponse(body as IPersonalityTestPayload);
  };

  const handleSubmitResponse = async (payload: IPersonalityTestPayload) => {
    try {
      setSubmitResponseLoading(true);
      await postPersonalityResponse(payload, userData?.email);

      if (!(currentStep < stepsData.length - 1)) {
        if (loggedIn) {
          const result = await handleResult();
          handleScrollToTop();
          setCurrentStep(currentStep + 1);
          await appwriteService.updateOnboardingPrefs({
            personality_test: result.map((item: string) => item[0]).join(''),
          });

          setUserData((prevUserData) => {
            if (!prevUserData) return undefined;

            const updatedAttributes = [
              ...(prevUserData.user_attributes || []),
              {
                attribute_key: 'personality_test',
                attribute_value: result.map((item: string) => item[0]).join(''),
              },
            ];
            return {
              ...prevUserData,
              user_attributes: updatedAttributes,
            };
          });
          const eventParams: EventDetails = {
            event_name: 'personality_test_complete',
            source: 'global',
            source_type: 'personality_test',
            source_id: null,
            sub_source: null,
            sub_source_id: null,
          };

          logEvent({
            eventDetails: eventParams,
            userData: userData,
            pathname: router.pathname,
          });

          Cookies.remove(COOKIE_NAME);
        } else {
          notify('Please Login to submit the test', 'warning');
          setVisible((prevState) => ({
            ...prevState,
            login: true,
            signUp: false,
            noRedirect: true,
          }));
        }
      } else {
        setCurrentStep(currentStep + 1);
        handleScrollToTop();
      }
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    } finally {
      setSubmitResponseLoading(false);
    }
  };

  const handleScrollToTop = () => {
    if (typeof window !== 'undefined') {
      programmaticScrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const handleResult = async () => {
    try {
      setLoading(true);
      const result = await getUserPersonalityAPI(userData?.email);
      setResults(result?.data);
      return result?.data;
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStepsData();
    loadSavedAnswers();
  }, []);

  useEffect(() => {
    const fetchLoggedInStatus = async () => {
      const loggedInStatus = await appwriteService.isLoggedIn();
      setLoggedIn(loggedInStatus);
    };
    fetchLoggedInStatus();
    if (isTokenChanged) {
      setIsTokenChanged(false);
    }
  }, [appwriteService.account, isTokenChanged]);

  const onCancelCloseModal = () => {
    setVisible((prevState) => ({
      ...prevState,
      signUp: false,
      subHeadingCopy: '',
      headingCopy: '',
    }));
  };

  const onCancelCloseLoginModal = () => {
    setVisible((prevState) => ({
      ...prevState,
      login: false,
      subHeadingCopy: '',
      headingCopy: '',
    }));
  };

  const onCancelCloseForgotPasswordModal = () => {
    setVisible((prevState) => ({
      ...prevState,
      forgotPassword: false,
    }));
  };

  return (
    <ConfigProvider theme={{ ...config, algorithm: [theme.darkAlgorithm] }}>
      <nav
        className={clsx(
          'flex py-2 fixed z-50 px-3 md:px-5 lg:px-12 border-b-2 border-borderDarkGray items-center justify-between w-full h-14 lg:min-h-16 top-0 bg-bgColor bg-opacity-75 backdrop-filter backdrop-blur-md',
        )}
      >
        <div className='flex  w-full  justify-start items-center cursor-pointer'>
          <span onClick={() => router.push('/')}>
            <Image
              src={ZebraLearnLogo}
              alt='Logo'
              priority
              className='object-contain w-[125px] h-[14px]'
            />
          </span>
        </div>
      </nav>

      <div
        ref={containerRef}
        id='containerRef'
        className='flex justify-center  bg-bgColor min-h-screen'
      >
        <div className={clsx('pt-20 pb-10 px-5 w-full md:w-2/3 lg:w-1/2')}>
          <ShouldRender check={!loading && !results?.length}>
            <div className=' flex flex-col  relative'>
              <ShouldRender
                check={
                  stepsData?.length &&
                  stepsData[currentStep]?.categoryId !== 'starting-page'
                }
              >
                <div className=' fixed md:static top-16 z-20 w-full -translate-y-2 bg-bgColor md:w-full '>
                  <span className=' customStepWhite flex justify-center w-full '>
                    <Steps
                      current={currentStep - 1}
                      size={screens.md ? 'default' : 'small'}
                      direction='horizontal'
                      className='stepTheme customStepWhite w-full pt-3 pb-3 px-5 -translate-x-5 md:-translate-x-0'
                      responsive={false}
                      items={stepsData.slice(1).map((_) => ({ title: null }))}
                    />
                  </span>
                </div>
              </ShouldRender>
              <ShouldRender
                check={
                  stepsData?.length &&
                  stepsData[currentStep]?.categoryId === 'starting-page'
                }
              >
                <PersonalityStart />
              </ShouldRender>

              <span className={clsx('pt-7 md:pt-4 ')}>
                <ShouldRender
                  check={currentStep !== 0 && currentStep < stepsData.length}
                >
                  <div className='flex justify-start -translate-x-4 mb-4'>
                    <Button
                      type='text'
                      onClick={() => setCurrentStep(currentStep - 1)}
                      className='text-base  hover:text-white bg-transparent border-none  text-[#787878] font-medium  rounded-lg flex items-center gap-2'
                    >
                      <ChevronLeftIcon className='w-5 h-5' /> Back
                    </Button>
                  </div>
                </ShouldRender>
                {stepsData[currentStep]?.questions.map((question, index) => (
                  <div
                    id={`question-${currentStep}-${index}`}
                    key={question.questionId}
                    className='pb-12'
                    ref={(el) => {
                      questionRefs.current[`${currentStep}-${index}`] = el;
                    }}
                  >
                    <p
                      className={clsx(
                        'pb-5 font-bold',
                        unansweredQuestions.includes(question.questionId) &&
                        'text-red-500',
                      )}
                    >
                      {question.text}
                    </p>
                    <div className='grid grid-cols-1 gap-4'>
                      {question.options.map((option) => (
                        <span className='group z-10' key={option.optionId}>
                          <motion.div
                            className={clsx(
                              'py-3 px-5 rounded-lg cursor-pointer flex items-center justify-between gap-2',
                              selectedOptions[question.questionId] ===
                                option.optionId
                                ? 'bg-[#515151]'
                                : 'bg-[#3D3D3D] group-hover:bg-[#515151]',
                            )}
                            onClick={() =>
                              handleOptionChange(
                                question.questionId,
                                option.optionId,
                                index,
                              )
                            }
                            whileHover={{ scale: 1.008 }}
                            whileTap={{ scale: 0.99 }}
                          >
                            <div className='flex items-center gap-2 '>
                              <div
                                className={clsx(
                                  'w-5 h-5 min-w-5 min-h-5 border rounded-full flex items-center justify-center',
                                  selectedOptions[question.questionId] ===
                                    option.optionId
                                    ? 'bg-white text-black'
                                    : 'border-[#515151] group-hover:border-[#3D3D3D]',
                                )}
                              >
                                {selectedOptions[question.questionId] ===
                                  option.optionId && <CheckIcon />}
                              </div>
                              {option.text}
                            </div>
                          </motion.div>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </span>

              <div
                className={clsx(
                  stepsData[currentStep]?.categoryId === 'starting-page'
                    ? 'flex flex-col items-center w-full gap-y-2'
                    : 'flex',
                  ' justify-center w-full gap-x-4',
                )}
              >
                <ButtonDefault
                  size={screens.md ? 2 : 1}
                  rounded={false}
                  variant={
                    !(
                      stepsData?.length &&
                      (stepsData[currentStep]?.categoryId === 'starting-page' ||
                        stepsData[currentStep]?.categoryId === 'end-page')
                    )
                      ? ButtonVariants.WHITE
                      : stepsData[currentStep]?.categoryId === 'end-page'
                        ? ButtonVariants.WHITE
                        : ButtonVariants.PRIMARY
                  }
                  is3D={
                    !!(
                      stepsData?.length &&
                      (stepsData[currentStep]?.categoryId === 'starting-page' ||
                        stepsData[currentStep]?.categoryId === 'end-page')
                    )
                  }
                  enableScaling={false}
                  loading={submitResponseLoading}
                  disabled={submitResponseLoading}
                  onClick={handleNext}
                  className='w-full text-center text-sm p-3  md:min-w-[720px] '
                >
                  <span className='md:px-1 py-1  whitespace-nowrap'>
                    {stepsData?.length &&
                      stepsData[currentStep]?.categoryId === 'starting-page'
                      ? 'Get Started'
                      : currentStep < stepsData.length - 1
                        ? 'Next'
                        : 'Submit'}
                  </span>
                </ButtonDefault>
                <ShouldRender
                  check={stepsData[currentStep]?.categoryId === 'starting-page'}
                >
                  <ButtonDefault
                    size={screens.md ? 2 : 1}
                    rounded={false}
                    variant={ButtonVariants.GRAY}
                    enableScaling={false}
                    loading={submitResponseLoading}
                    disabled={submitResponseLoading}
                    onClick={() => router.push('/')}
                    className='w-full text-center text-sm p-3  md:min-w-[720px]'
                  >
                    <span className='md:px-1 py-1 font-semibold   whitespace-nowrap'>
                      Skip
                    </span>
                  </ButtonDefault>
                </ShouldRender>
              </div>
            </div>
          </ShouldRender>

          <ShouldRender
            check={
              !loading && results?.length && currentStep > stepsData.length - 1
            }
          >
            <ResultPage results={results as string[]} />
          </ShouldRender>
        </div>

        <ShouldRender check={loading}>
          <CustomLoader />
        </ShouldRender>
      </div>
      <SignUpModal
        onCancel={onCancelCloseModal}
        visible={visible.signUp}
        noRedirect={visible?.noRedirect}
      />
      <LoginModal onCancel={onCancelCloseLoginModal} visible={visible.login} />
      <ForgotPasswordModal
        onCancel={onCancelCloseForgotPasswordModal}
        visible={visible.forgotPassword}
      />
    </ConfigProvider>
  );
};

export default TestPage;
